import React, { createContext, useContext, ReactNode, useEffect, useState } from 'react';

declare global {
  interface Window {
    grecaptcha: {
      enterprise: {
        ready: (callback: () => void) => void;
        execute: (siteKey: string, options: { action: string }) => Promise<string>;
      };
    };
  }
}

interface RecaptchaEnterpriseContextType {
  isLoaded: boolean;
  executeRecaptcha: (action: string) => Promise<string | null>;
  siteKey: string;
}

const RecaptchaEnterpriseContext = createContext<RecaptchaEnterpriseContextType | null>(null);

export interface RecaptchaEnterpriseProviderProps {
  children: ReactNode;
  siteKey: string;
}

export const RecaptchaEnterpriseProvider: React.FC<RecaptchaEnterpriseProviderProps> = ({ children, siteKey }) => {
  const [isLoaded, setIsLoaded] = useState(false);

  const executeRecaptcha = async (action: string): Promise<string | null> => {
    if (!window.grecaptcha?.enterprise) {
      console.error('reCAPTCHA Enterprise not available');
      return null;
    }

    return new Promise((resolve) => {
      window.grecaptcha.enterprise.ready(() => {
        window.grecaptcha.enterprise
          .execute(siteKey, { action })
          .then(resolve)
          .catch(() => resolve(null));
      });
    });
  };

  useEffect(() => {
    // Check if grecaptcha.enterprise is already available
    if (window.grecaptcha?.enterprise) {
      setIsLoaded(true);
      return;
    }

    // Check if script already exists
    if (document.querySelector('script[src*="recaptcha/enterprise.js"]')) {
      // Script exists but grecaptcha might not be ready yet
      const checkReady = () => {
        if (window.grecaptcha?.enterprise) {
          setIsLoaded(true);
        } else {
          setTimeout(checkReady, 100);
        }
      };
      checkReady();
      return;
    }

    // Load script
    const script = document.createElement('script');
    script.src = `https://www.google.com/recaptcha/enterprise.js?render=${siteKey}`;
    script.onload = () => {
      // Wait for grecaptcha.enterprise to be available
      const checkReady = () => {
        if (window.grecaptcha?.enterprise) {
          setIsLoaded(true);
        } else {
          setTimeout(checkReady, 100);
        }
      };
      checkReady();
    };
    document.head.appendChild(script);
  }, [siteKey]);

  const contextValue: RecaptchaEnterpriseContextType = {
    isLoaded,
    executeRecaptcha,
    siteKey,
  };

  return <RecaptchaEnterpriseContext.Provider value={contextValue}>{children}</RecaptchaEnterpriseContext.Provider>;
};

export const useRecaptchaEnterpriseContext = (): RecaptchaEnterpriseContextType => {
  const context = useContext(RecaptchaEnterpriseContext);
  if (!context) {
    throw new Error('useRecaptchaEnterpriseContext must be used within a RecaptchaEnterpriseProvider');
  }
  return context;
};
